import { useEffect, useRef, useState } from 'react'
import { tickSocket, type BookUpdate } from '@/feed/tickSocket'

export const BOOK_REFRESH_MS = 5_000

/**
 * A ready notification is the fast path; reconnect/focus/poll repair missed ones.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PATCH WHEN THE NOTICE CARRIES THE ANSWER, RE-READ WHEN IT DOES NOT
 *
 * This used to bump a revision for every notice, and the caller answered each
 * one with a full `GET /trades/open` — a round trip from the user's own
 * connection to learn numbers the server already had when it sent the notice.
 * On a 150 ms link that is 300 ms per fill, for no new information.
 *
 * `BOOK_UPDATED` now carries the changed legs when the change can be expressed
 * as a patch. When it does, `onPatch` applies them and NO revision is bumped,
 * so nothing refetches and nothing remounts — which also means a panel the
 * trader is typing into survives a fill landing underneath it.
 *
 * When the legs are absent the shape of the book moved — a status transition,
 * a failed leg — and the old behaviour is exactly right: bump, and re-read.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useBookUpdates(onUpdate?: (u: BookUpdate) => boolean): number {
  const [revision, setRevision] = useState(0)
  // Kept in a ref so a caller passing an inline arrow does not re-subscribe
  // on every render — the socket listener is set up once, deliberately.
  const handle = useRef(onUpdate)
  handle.current = onUpdate
  useEffect(() => {
    let coalescing: ReturnType<typeof setTimeout> | undefined
    const refresh = () => {
      if (coalescing !== undefined) return
      coalescing = setTimeout(() => {
        coalescing = undefined
        setRevision((n) => n + 1)
      }, 75)
    }
    const visible = () => { if (document.visibilityState !== 'hidden') refresh() }
    const offBook = tickSocket.onBookUpdated((u) => {
      /*
       * THE HANDLER DECIDES, and says so by returning true. It owns the book,
       * so it is the only thing that knows whether what arrived was enough —
       * a patch it could apply, a trade it could insert, a close it could
       * act on. Anything it declines falls back to a re-read, which is the
       * behaviour that was here before any of this and is never wrong.
       */
      if (u && handle.current?.(u) === true) return
      refresh()
    })
    const offState = tickSocket.onState((state) => { if (state === 'OPEN') refresh() })
    const timer = setInterval(visible, BOOK_REFRESH_MS)
    window.addEventListener('focus', visible)
    document.addEventListener('visibilitychange', visible)
    return () => {
      offBook()
      offState()
      clearInterval(timer)
      clearTimeout(coalescing)
      window.removeEventListener('focus', visible)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [])
  return revision
}
